<script setup lang="ts">
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { AlertCircle } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Checkbox } from '~/components/ui/checkbox'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select'
import type { FormServerErrorResponse, ILocation, IResponseData } from '~/types'
import { useApiPost } from '~/composables/useApiPost'
import { WidgetPhoneInput } from '~/components/widget/phone-input'
import { ContentBlock } from '~/components/ui/content-block'
import { ANCHOR_IDS } from '~/lib/constants'
import { AGE_OPTIONS, WORKPLACE_OPTIONS, APP_OPTIONS, APP_NAME } from './mocks'
import { useToast } from '~/components/ui/toast'

const config = useRuntimeConfig()
const { toast } = useToast()

// Pre-compute all validation messages to avoid calling useValidationMessage in computed
const validationMessages = {
  requiredFieldLastName: useValidationMessage('requiredFieldLastName'),
  minValue2: useValidationMessage('minValue', 2),
  maxValue50: useValidationMessage('maxValue', 50),
  requiredFieldFirstName: useValidationMessage('requiredFieldFirstName'),
  requiredEmailInvalid: useValidationMessage('requiredEmailInvalid'),
  requiredCity: useValidationMessage('requiredCity'),
  requiredField: useValidationMessage('requiredField'),
  requiredFieldAge: useValidationMessage('requiredFieldAge'),
  requiredFieldWorkplace: useValidationMessage('requiredFieldWorkplace'),
  agreement: useValidationMessage('agreement'),

}

const apiParams = {
  per_page: 100
}

const props = defineProps<{
  title?: string
  headingTag?: string
  organizationType: string
  organizationId: number
  cardId?: number
  cardExtendedForm?: boolean
  disableAnchor?: boolean
  variant?: 'white' | 'blue' | 'default'
}>()

const { data } = useApi<IResponseData<ILocation[]>>(
  () => useUrlWithParams('/locations', apiParams),
  {
    key: 'card-request-form-locations'
  }
)
const cities = computed(() => data.value?.data)

const formSchema = computed(() =>
  toTypedSchema(
    z.object({
      last_name: z
        .string({ message: validationMessages.requiredFieldLastName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      first_name: z
        .string({ message: validationMessages.requiredFieldFirstName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      phone: usePhoneValidationSchema(),
      email: z
        .string()
        .email({ message: validationMessages.requiredEmailInvalid })
        .optional()
        .or(z.literal('')),
      location_id: z.coerce.number().min(1, { message: validationMessages.requiredCity }),
      ...(props.cardExtendedForm
        ? {
            age: z
              .string({ message: validationMessages.requiredField })
              .min(1, { message: validationMessages.requiredFieldAge }),
            workplace: z
              .string({ message: validationMessages.requiredField })
              .min(1, { message: validationMessages.requiredFieldWorkplace }),
            app_usage: z
              .string({ message: validationMessages.requiredField })
              .min(1, { message: validationMessages.requiredField })
          }
        : {}),
      agreement: z
        .boolean({
          required_error: validationMessages.agreement
        })
        .refine((val) => val, {
          message: validationMessages.agreement
        })
    })
  )
)

const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    location_id: undefined,
    agreement: false,
    email: '',
    age: '',
    workplace: '',
    app_usage: ''
  }
})

watch(
  cities,
  (newCities) => {
    if (newCities?.length && !form.values.location_id) {
      form.setFieldValue('location_id', newCities[0].id)
    }
  },
  { immediate: true }
)

const { alert, requestId, formBlock, handleSuccessSubmit } = useFormRequestSuccess()
const { trackEvent } = useAnalytics()

const handleServerValidationErrors = <T extends string>(
  error: unknown,
  setFieldError: (field: T, message: string | string[] | undefined) => void,
  knownFields: T[]
) => {
  if (typeof error === 'object' && error !== null && '_data' in error) {
    const serverError = error as FormServerErrorResponse
    const errors = serverError._data?.errors
    const message = serverError._data?.message

    if (errors && typeof errors === 'object') {
      for (const [key, messages] of Object.entries(errors)) {
        if (Array.isArray(messages) && messages.length) {
          if (knownFields.includes(key as T)) {
            setFieldError(key as T, messages[0])
          }
        }
      }
    } else if (message) {
      toast({
        title: message,
        variant: 'destructive'
      })
    }
  }

}

const onSubmit = form.handleSubmit(async (values) => {
  const requestBody: Record<string, unknown> = {
    leadable_type: 'card',
    leadable_id: props.cardId ?? null,
    organization_type: props.organizationType,
    organization_id: props.organizationId,
    location_id: values.location_id,
    last_name: values.last_name,
    first_name: values.first_name,
    phone: `+996${values.phone}`,
    email: values.email || '',
    fingerprint_id: useFingerprintStore().visitorId,
    ...(props.cardExtendedForm
      ? {
          age: values.age,
          workplace: values.workplace,
          app_usage: values.app_usage
        }
      : {})
  }

  const { data, error } = await useApiPost<IResponseData<{ id: string }>>('/leads', requestBody)
  if (error) {
    handleServerValidationErrors(error, form.setFieldError, [
      'last_name',
      'first_name',
      'phone',
      'email',
      'location_id',
      ...(props.cardExtendedForm ? ['age', 'workplace', 'app_usage'] : []),
      'agreement'
    ])
    trackEvent('lead_form_error', { lead_type: 'card' })
  } else {
    trackEvent('generate_lead', {
      lead_type: 'card',
      product_id: props.cardId,
      organization_id: props.organizationId,
      organization_type: props.organizationType
    })
    await handleSuccessSubmit(data?.data.id ?? '')
  }
})
</script>

<template>
  <ContentBlock
    :id="disableAnchor ? undefined : ANCHOR_IDS.FEATURE_CARD_FORM_REQUEST"
    ref="formBlock"
    :variant="variant ?? 'white'"
    :heading-tag="headingTag ?? 'h3'"
    :heading-text="props.title ?? $t('feature.card_form_request.heading')"
    :show-link-icon="!disableAnchor"
    class="FormScrollOffset"
  >
    <Alert v-if="alert" variant="green">
      <AlertCircle class="h-4 w-4" />
      <AlertTitle>{{ $t('feature.card_form_request.success.title') }}</AlertTitle>
      <AlertDescription>
        <div>
          {{ $t('feature.card_form_request.success.description') }}
        </div>
        <div>{{ $t('feature.card_form_request.success.request_id') }}{{ requestId }}.</div>
      </AlertDescription>
    </Alert>
    <div v-else class="space-y-5">
      <p>
        {{ $t('feature.card_form_request.description') }}
      </p>
      <form class="flex flex-col gap-5" @submit="onSubmit">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField v-slot="{ componentField, errors }" name="last_name">
            <FormItem>
              <FormLabel>{{ $t('common.last_name') }}*</FormLabel>
              <FormControl>
                <Input type="text" :placeholder="$t('common.last_name')" v-bind="componentField" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField, errors }" name="first_name">
            <FormItem>
              <FormLabel>{{ $t('common.first_name') }}*</FormLabel>
              <FormControl>
                <Input v-bind="componentField" :placeholder="$t('common.first_name')" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField, errors }" name="phone">
            <FormItem>
              <FormLabel>{{ $t('common.phone') }}*</FormLabel>
              <FormControl>
                <WidgetPhoneInput v-bind="componentField" :errors="errors.length" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField, errors }" name="email">
            <FormItem>
              <FormLabel>{{ $t('common.email') }}</FormLabel>
              <FormControl>
                <Input type="email" v-bind="componentField" :placeholder="$t('common.email')" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <ClientOnly>
            <FormField v-slot="{ componentField, errors }" name="location_id">
              <FormItem :class="cardExtendedForm ? '' : 'md:col-span-2'">
                <FormLabel>{{ $t('feature.card_form_request.labels.location') }}</FormLabel>
                <Select v-bind="componentField">
                  <SelectTrigger
                    :aria-label="$t('common.select_city')"
                    :aria-invalid="errors.length > 0"
                  >
                    <SelectValue :placeholder="$t('common.select_city')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="city in cities" :key="city.id" :value="city.id">{{
                      city.title
                    }}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage v-if="errors.length" :message="errors[0]" />
              </FormItem>
            </FormField>

            <template v-if="cardExtendedForm">
              <FormField v-slot="{ componentField, errors }" name="age">
                <FormItem>
                  <FormLabel>{{ $t('common.form_request.labels.age') }}</FormLabel>
                  <Select v-bind="componentField">
                    <SelectTrigger :aria-invalid="errors.length > 0">
                      <SelectValue :placeholder="$t('common.form_request.labels.age')" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        v-for="option in AGE_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ $t(option.label) }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage v-if="errors.length" :message="errors[0]" />
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField, errors }" name="workplace">
                <FormItem>
                  <FormLabel>{{ $t('common.form_request.labels.workplace') }}</FormLabel>
                  <Select v-bind="componentField">
                    <SelectTrigger :aria-invalid="errors.length > 0">
                      <SelectValue :placeholder="$t('common.form_request.labels.workplace')" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        v-for="option in WORKPLACE_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ $t(option.label) }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage v-if="errors.length" :message="errors[0]" />
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField, errors }" name="app_usage">
                <FormItem>
                  <FormLabel>{{
                    $t('common.form_request.labels.app_name', { app_name: APP_NAME })
                  }}</FormLabel>
                  <Select v-bind="componentField">
                    <SelectTrigger :aria-invalid="errors.length > 0">
                      <SelectValue
                        :placeholder="
                          $t('common.form_request.labels.app_name', { app_name: APP_NAME })
                        "
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        v-for="option in APP_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ $t(option.label) }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage v-if="errors.length" :message="errors[0]" />
                </FormItem>
              </FormField>
            </template>
          </ClientOnly>
        </div>

        <FormField v-slot="{ value, errorMessage, handleChange }" type="checkbox" name="agreement">
          <FormItem>
            <FormControl>
              <div class="flex items-center space-x-2">
                <Checkbox id="terms" :model-value="value" @update:model-value="handleChange" />
                <Label for="terms">
                  {{ $t('feature.card_form_request.agreement') }}
                  <NuxtLinkLocale to="/privacy-policy" class="text-blue-600 underline">
                    {{ $t('feature.card_form_request.agreement_link') }}
                  </NuxtLinkLocale>
                </Label>
              </div>
            </FormControl>
            <FormMessage v-if="errorMessage" :message="errorMessage" />
          </FormItem>
        </FormField>

        <div class="flex flex-col items-start gap-4 md:flex-row md:justify-between">
          <Button type="submit" class="mt-4 md:mt-0">{{
            $t('feature.card_form_request.submit')
          }}</Button>
        </div>
      </form>
    </div>
  </ContentBlock>
</template>
